/**
 * AIModelsManagement — root sub-page for AI Models.
 *
 * Layout (top → bottom):
 *   1) LedgerOverview (USD) — always visible at top.
 *   2) Two model cards → clicking opens the per-provider sub-view.
 *
 * Per-provider sub-view:
 *   - USD ProviderPanel (Tokens + cost_usd — Credits removed 2026-02).
 *   - Gemini also shows GeminiLimitsCard.
 *   - Pricing block always at the bottom of the sub-view.
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Activity, BrainCircuit, Cpu, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import DeepSeekUsage from "@/pages/admin/DeepSeekUsage";
import { pricingApi, ledgerApi, configApi } from "@/lib/api";
import {
  LedgerOverview, DepositForm, ThresholdForm, MigrationPanel, ProviderPanel,
} from "@/pages/admin/UsdLedgerManagement";
import GeminiLimitsCard from "@/pages/admin/components/GeminiLimitsCard";

function PricingSection({ model, canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    pricingApi.get(model)
      .then(d => { setData(d); setForm({...d}); })
      .catch(e => setErr(e?.response?.data?.detail || "تعذّر تحميل التسعيرة"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [model]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = model === "gemini"
        ? { input_per_1m_usd: +form.input_per_1m_usd, output_per_1m_usd: +form.output_per_1m_usd, per_image_usd: +form.per_image_usd }
        : { input_per_1m_usd: +form.input_per_1m_usd, output_per_1m_usd: +form.output_per_1m_usd };
      const upd = await pricingApi.put(model, body);
      setData(upd); setForm({...upd});
      toast.success("تم تحديث التسعيرة — تُطبَّق على الاستخدامات المستقبلية فقط");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.[0]?.msg || "تعذّر الحفظ"));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (err) return <div data-testid={`pricing-${model}-error`} className="p-4 rounded-2xl bg-red-50 border-2 border-red-200 text-red-800 text-sm">{err}</div>;

  const fields = model === "gemini"
    ? [
        { k: "input_per_1m_usd", label: "سعر Input Tokens (USD لكل مليون)" },
        { k: "output_per_1m_usd", label: "سعر Output Tokens (USD لكل مليون)" },
        { k: "per_image_usd", label: "سعر الصورة/الطلب (USD)" },
      ]
    : [
        { k: "input_per_1m_usd", label: "سعر Input Tokens (USD لكل مليون)" },
        { k: "output_per_1m_usd", label: "سعر Output Tokens (USD لكل مليون)" },
      ];

  return (
    <form onSubmit={save} data-testid={`pricing-${model}-form`}
      className="bg-white border-2 border-slate-200 rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-emerald-600" />
        <h3 className="font-bold text-lg text-slate-900">إعدادات التسعيرة</h3>
        {!canEdit && <span className="text-xs text-slate-500">(للعرض فقط — super_admin فقط يستطيع التعديل)</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {fields.map(f => (
          <div key={f.k}>
            <label className="block text-xs font-bold text-slate-500 mb-1">{f.label}</label>
            <input type="number" min="0" step="0.000001" disabled={!canEdit || saving}
              data-testid={`pricing-${model}-${f.k}`}
              value={form?.[f.k] ?? 0}
              onChange={e => setForm(x => ({...x, [f.k]: e.target.value}))}
              className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold disabled:opacity-70" dir="ltr" />
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">
        الوحدة: <b>{data?.unit}</b>. تُطبَّق على السجلات المستقبلية فقط — لا يُعاد حساب أي تكلفة تاريخية.
        {data?.updated_by && <> · آخر تعديل بواسطة <b>{data.updated_by}</b></>}
      </p>
      {canEdit && (
        <div className="flex justify-end">
          <button type="submit" disabled={saving} data-testid={`pricing-${model}-save`}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-50 cursor-pointer">
            {saving ? "..." : "حفظ التسعيرة"}
          </button>
        </div>
      )}
    </form>
  );
}


export default function AIModelsManagement({ onBack, me }) {
  const [sub, setSub] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loadingHead, setLoadingHead] = useState(true);
  const canView = me?.is_super_admin || (me?.permissions || []).includes("view_analytics");
  const canEditPricing = !!me?.is_super_admin;
  const canEditLedger = !!me?.is_super_admin;

  const reloadOverview = useCallback(async () => {
    try { setOverview(await ledgerApi.overview()); }
    catch (e) { /* silent — overview is informational only */ }
  }, []);

  // Fetch the V2 feature flag once. Default ON when the config endpoint
  // fails — legacy Credits UI was removed 2026-02 so there is no fallback.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await configApi.get();
        if (alive) setCfg(c);
        if (alive && c?.usd_ledger_v2_enabled !== false) await reloadOverview();
      } catch {
        if (alive) setCfg({ usd_ledger_v2_enabled: true });
      } finally { if (alive) setLoadingHead(false); }
    })();
    return () => { alive = false; };
  }, [reloadOverview]);

  const v2 = cfg?.usd_ledger_v2_enabled !== false;

  // Per-provider sub-view.
  if (sub === "gemini") return (
    <div className="space-y-6">
      <div className="border-b-2 border-slate-100 pb-4">
        <button type="button" onClick={() => setSub(null)} data-testid="ai-provider-back"
          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold px-3 py-2 rounded-lg cursor-pointer text-sm">
          <ArrowRight className="w-4 h-4" /><span>العودة</span>
        </button>
      </div>
      <ProviderPanel provider="gemini" />
      <GeminiLimitsCard canEdit={canEditPricing} />
      <PricingSection model="gemini" canEdit={canEditPricing} />
    </div>
  );
  if (sub === "deepseek") return (
    <div className="space-y-6">
      <div className="border-b-2 border-slate-100 pb-4">
        <button type="button" onClick={() => setSub(null)} data-testid="ai-provider-back"
          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold px-3 py-2 rounded-lg cursor-pointer text-sm">
          <ArrowRight className="w-4 h-4" /><span>العودة</span>
        </button>
      </div>
      <ProviderPanel provider="deepseek" />
      <PricingSection model="deepseek" canEdit={canEditPricing} />
    </div>
  );

  return (
    <div data-testid="ai-models-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b-2 border-slate-900 pb-6">
        <div>
          <div className="text-sm font-bold text-slate-500 mb-2">دوائي / لوحة الإدارة</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <Cpu className="w-7 h-7" />نماذج الذكاء الاصطناعي
          </h1>
          <p className="text-sm text-slate-500 mt-1">النظرة العامة للاستهلاك، إعدادات وتسعيرة كل نموذج</p>
        </div>
        <button type="button" onClick={onBack} data-testid="ai-back-btn"
          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold px-4 py-3 rounded-xl cursor-pointer">
          <ArrowRight className="w-4 h-4" /><span>العودة إلى لوحة الأدمن</span>
        </button>
      </div>

      {loadingHead && (
        <div className="p-6 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      )}

      {!loadingHead && overview && canView && (
        <div data-testid="ai-ledger-overview" className="space-y-4">
          <LedgerOverview data={overview} onReload={reloadOverview} />
          {canEditLedger && (
            <div className="grid grid-cols-1 gap-4">
              <DepositForm canEdit onSuccess={reloadOverview} />
              <ThresholdForm canEdit current={overview?.low_balance_threshold_usd || 0} onSuccess={reloadOverview} />
              <MigrationPanel canEdit onSuccess={reloadOverview} />
            </div>
          )}
        </div>
      )}

      {!canView ? (
        <div className="p-6 bg-amber-50 border-2 border-amber-200 rounded-2xl text-amber-900 text-sm">
          لا تملك صلاحية رؤية الإحصائيات. اطلب من الأدمن الرئيسي منحك صلاحية <b>رؤية الإحصائيات</b>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button type="button" data-testid="ai-open-gemini" onClick={() => setSub("gemini")}
            className="text-right flex flex-col gap-2 border-2 border-emerald-200 hover:border-emerald-300 bg-emerald-50 rounded-2xl p-6 min-h-[130px] cursor-pointer">
            <Activity className="w-8 h-8 text-emerald-700" />
            <span className="font-bold text-xl text-slate-900">Gemini</span>
            <span className="text-sm text-slate-600">الاستهلاك بالدولار · Tokens · السجلات (OCR)</span>
          </button>
          <button type="button" data-testid="ai-open-deepseek" onClick={() => setSub("deepseek")}
            className="text-right flex flex-col gap-2 border-2 border-blue-200 hover:border-blue-300 bg-blue-50 rounded-2xl p-6 min-h-[130px] cursor-pointer">
            <BrainCircuit className="w-8 h-8 text-blue-700" />
            <span className="font-bold text-xl text-slate-900">DeepSeek</span>
            <span className="text-sm text-slate-600">محاولات API · Tokens · معدل النجاح · السجلات</span>
          </button>
        </div>
      )}
    </div>
  );
}
